import { useState } from 'react';
import { ArrowRight, Download, TrendingUp, BarChart3, Users, Shield, Brain, Zap, Globe, CheckCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import SEOHead from '@/components/SEOHead';
import { DemoModal } from '@/components/DemoModal';

var KEY_FINDINGS = [
  { stat: "26,800 Cr", label: "India's Channel Loyalty Market Size (INR)", icon: TrendingUp },
  { stat: "51%", label: "Partners enrolled in 6+ programs but engage with few", icon: Users },
  { stat: "95%", label: "Brands adopting sustainable loyalty frameworks", icon: Shield },
  { stat: "17.7%", label: "Annual market growth rate (2025-2026)", icon: BarChart3 },
  { stat: "96%", label: "Retailers prefer WhatsApp-based engagement", icon: Zap },
  { stat: "60%", label: "Shift from sell-in to sell-out driven loyalty", icon: Globe },
];

var REPORT_SECTIONS = [
  { title: "Market Size & Growth", desc: "India's channel loyalty market valued at Rs 26,800 crore with 17.7% CAGR. Projected to reach $7.18 billion by 2030. The shift from incentive-heavy to outcome-driven models is accelerating." },
  { title: "The Engagement Fatigue Problem", desc: "51% of channel partners are enrolled in 6+ loyalty programs but actively engage with only a handful. Brands that fail to differentiate their programs face declining participation and ROI." },
  { title: "AI-Driven Personalization", desc: "Machine learning enables micro-segmentation of dealer, distributor, and retailer cohorts. Predictive churn models identify at-risk partners 30-60 days before disengagement, enabling proactive intervention." },
  { title: "WhatsApp & Mobile-First", desc: "96% of retailers prefer WhatsApp as their primary engagement channel. Successful programs embed enrollment, rewards, communication, and support into WhatsApp workflows." },
  { title: "Sell-Out Over Sell-In", desc: "The fundamental shift from invoice-based (sell-in) to secondary sales (sell-out) tracking. QR-verified purchase data replaces unreliable billing claims, reducing scheme leakage by 60-85%." },
  { title: "Instant Gratification & UPI", desc: "UPI-based instant payouts have become table stakes. Programs with same-day UPI rewards see 3.2x higher engagement than traditional voucher/points-based models." },
  { title: "Compliance & Governance", desc: "TDS auto-calculation, GST compliance, and digital KYC are now mandatory. Platforms that automate compliance save brands 200+ hours per quarter in manual processing." },
  { title: "Industry-Specific Insights", desc: "Deep dives into Cement, FMCG, Automotive, Paints, Pharma, and Electrical — each with unique channel dynamics, stakeholder maps, and loyalty strategy recommendations." },
];

var RECOMMENDATIONS = [
  "Invest in AI-powered loyalty platforms that enable real-time personalization and predictive analytics",
  "Shift from transactional rewards to behavior-linked incentives tied to secondary sales",
  "Deploy WhatsApp-native engagement workflows for maximum partner adoption",
  "Implement QR-based product verification to reduce counterfeit and scheme leakage",
  "Build partner certification and education programs that drive long-term brand preference",
  "Automate TDS, GST, and KYC compliance to reduce operational overhead",
  "Create tiered loyalty structures that reward consistency over volume",
  "Measure program ROI through sell-out data, not just sell-in metrics",
];

export default function ChannelLoyaltyReportPage() {
  var [demoOpen, setDemoOpen] = useState(false);

  var reportJsonLd = {
    "@context": "https://schema.org",
    "@type": "Report",
    "name": "Channel Loyalty Report 2026 - India B2B Loyalty Market Analysis",
    "author": {"@type": "Organization", "name": "ChannelLoyalty"},
    "datePublished": "2026-03-01",
    "description": "Comprehensive analysis of India's Rs 26,800 crore channel loyalty market with insights on AI-driven engagement, sell-out loyalty, and industry-specific strategies.",
    "url": "https://channelloyalty.ai/channel-loyalty-report-2026"
  };

  return (
    <div data-testid="channel-loyalty-report-page">
      <Navbar onDemoClick={function() { setDemoOpen(true); }} />
      <SEOHead
        title="Channel Loyalty Report 2026 - India B2B Loyalty Market Analysis | ChannelLoyalty"
        description="Download ChannelLoyalty's Channel Loyalty Report 2026. Comprehensive analysis of India's Rs 26,800 crore channel loyalty market — AI-driven engagement, sell-out loyalty trends, and industry strategies."
        keywords="channel loyalty report 2026, India B2B loyalty market, channel partner engagement report, dealer loyalty trends India, channel incentive market size"
        canonical="https://channelloyalty.ai/channel-loyalty-report-2026"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reportJsonLd) }} />

      {/* Hero */}
      <section className="hero-full" style={{ paddingBottom: '60px' }}>
        <div className="container text-center pt-12">
          <div className="hero-badge mx-auto mb-6"><BarChart3 size={16} /> Industry Report</div>
          <h1 className="text-white mb-4">Channel Loyalty Report 2026</h1>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            India's Rs 26,800 crore channel loyalty market is undergoing a fundamental shift — from incentive-heavy schemes to AI-driven, outcome-focused engagement. Here's what every brand needs to know.
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <button onClick={function() { setDemoOpen(true); }} className="btn btn-primary">
              <Download size={18} /> Get Full Report
            </button>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="section-light section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Key Findings</span>
            <h2>The Numbers That Matter</h2>
          </div>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {KEY_FINDINGS.map(function(kf, idx) {
              var Icon = kf.icon;
              return (
                <div key={idx} className="stat-card" data-testid={"key-finding-" + idx}>
                  <Icon size={24} style={{ color: '#6366f1', marginBottom: '8px' }} />
                  <div className="stat-number" style={{ fontSize: '32px' }}>{kf.stat}</div>
                  <div className="stat-label" style={{ fontSize: '13px' }}>{kf.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Report Sections */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Report Highlights</span>
            <h2>What's Inside the Report</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            {REPORT_SECTIONS.map(function(sec, idx) {
              return (
                <div key={idx} className="card" style={{ padding: '28px' }}>
                  <h4 style={{ fontSize: '17px', fontWeight: 700, color: '#1e293b', marginBottom: '10px' }}>{sec.title}</h4>
                  <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.7 }}>{sec.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recommendations */}
      <section className="section-light section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Strategic Recommendations</span>
            <h2>What Brands Should Do in 2026</h2>
          </div>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {RECOMMENDATIONS.map(function(rec, idx) {
              return (
                <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '20px', padding: '16px 20px', background: 'white', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>{idx + 1}</div>
                  <p style={{ fontSize: '15px', color: '#334155', lineHeight: 1.6, margin: 0 }}>{rec}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container relative z-10 text-center">
          <h2>Get the Full Report</h2>
          <p>Download the complete Channel Loyalty Report 2026 with industry deep dives, data tables, and implementation roadmaps.</p>
          <div className="cta-buttons" style={{ justifyContent: 'center' }}>
            <button onClick={function() { setDemoOpen(true); }} className="btn btn-primary">
              <Download size={18} /> Download Report
            </button>
          </div>
        </div>
      </section>

      <MegaFooter />
      <DemoModal isOpen={demoOpen} onClose={function() { setDemoOpen(false); }} />
    </div>
  );
}
